import React from 'react';
import classNames from 'classnames';
import { Button } from 'antd';
import SystemModal from 'components/Shared/Modal';
import useTranslation from 'utils/hooks/useTranslation';

import styles from './style.module.scss';

const KEY_PREFIX = 'corporateAccounts.wallet.dashboard.addFunds';

// Step 1 of the bank transfer flow: the customer sends the transfer from their own bank using
// these details, then comes back and confirms it in step 2 (ConfirmTransferModal).
// `bankDetails` is the [labelKey, value] list for the wallet's country; `wireReference` is the
// reference the Wallet Service issued for this wallet — without it a transfer can't be matched.
const SendTransferModal = ({
  visible,
  onClose,
  onNext,
  onCopyBankDetail,
  bankDetails = [],
  wireReference,
}) => {
  const { translateText } = useTranslation();

  // Copying a detail is the point of no return for the dashboard: the transfer is about to happen
  // in the customer's own bank, so a confirmation is owed from here on.
  const handleCopy = value => {
    navigator.clipboard.writeText(value);
    if (onCopyBankDetail) {
      onCopyBankDetail();
    }
  };

  const renderValue = (labelKey, value) => (
    <span className={styles.bankDetailValueWrap}>
      <span
        // Customers copy these into their bank, so browser translation and translation extensions
        // must leave them as they are; only the label translates. `notranslate` covers translators
        // that ignore `translate`.
        className={classNames(styles.bankDetailValue, 'notranslate')}
        translate="no"
        data-testid={`walletAddFundsValue-${labelKey}`}
      >
        {value}
      </span>
      <a
        className={styles.bankDetailCopy}
        // Copies the value itself, never the text on screen
        onClick={() => handleCopy(value)}
        data-testid={`walletAddFundsCopy-${labelKey}`}
      >
        {translateText('corporateAccounts.common.copy')}
      </a>
    </span>
  );

  return (
    <SystemModal
      title={
        <>
          <div className={styles.dashboardModalStep}>
            {translateText(`${KEY_PREFIX}.sendTransferModal.step`)}
          </div>
          <span className={styles.dashboardModalTitle}>
            {translateText(`${KEY_PREFIX}.sendTransferModal.title`)}
          </span>
        </>
      }
      titleLevel={5}
      width={440}
      visible={visible}
      onCancel={onClose}
      footer={null}
      bodyStyle={{ padding: '20px' }}
      className={`${styles.dashboardModal} ${styles.sendTransferModal}`}
      destroyOnClose
      centered
      data-testid="sendTransferModal"
    >
      <div className={styles.confirmTransferCopy}>
        {translateText(`${KEY_PREFIX}.sendTransferModal.copy`)}
      </div>

      <div className={styles.bankDetailsGrid} data-testid="walletAddFundsBankDetails">
        {bankDetails.map(([labelKey, value]) => (
          <React.Fragment key={labelKey}>
            <span className={styles.bankDetailLabel}>
              {translateText(`${KEY_PREFIX}.bankDetails.${labelKey}`)}
            </span>
            {renderValue(labelKey, value)}
          </React.Fragment>
        ))}
      </div>

      {wireReference && (
        <div className={styles.bankReference} data-testid="walletAddFundsReference">
          <div className={styles.bankReferenceRow}>
            <span className={styles.bankDetailLabel}>
              {translateText(`${KEY_PREFIX}.bankDetails.reference`)}
            </span>
            {renderValue('reference', wireReference)}
          </div>
          <div className={styles.bankReferenceHint}>
            {translateText(`${KEY_PREFIX}.sendTransferModal.referenceHint`)}
          </div>
        </div>
      )}

      <Button
        type="primary"
        block
        className={styles.confirmTransferSubmit}
        onClick={onNext}
        data-testid="sendTransferModalNext"
      >
        {translateText(`${KEY_PREFIX}.sendTransferModal.sent`)}
      </Button>
      <div className={styles.sendTransferFootnote}>
        {translateText(`${KEY_PREFIX}.sendTransferModal.notReady`)}
      </div>
    </SystemModal>
  );
};

export default SendTransferModal;
